/**
 * X25519 wrap of the family key for delivery to an endpoint, byte-compatible
 * with `backstop_crypto::x25519`.
 *
 * We use age's `age1…`/`AGE-SECRET-KEY-1…` bech32 encoding via the
 * `age-encryption` package so the parent PWA and the endpoint speak the same
 * wire format: wrapping produces an age v1 file with an X25519 recipient
 * stanza, and the endpoint's `unwrap_family_key_for_endpoint` (Rust) accepts
 * the exact bytes.
 *
 * A "wrap" here is an age-encrypted blob whose plaintext is the 32-byte
 * family key. Unwrapping recovers those 32 bytes.
 */

import {
  Decrypter,
  Encrypter,
  generateX25519Identity,
  identityToRecipient,
} from 'age-encryption';

/** A freshly generated endpoint keypair, ready to be stored / shared. */
export interface EndpointKeypair {
  /** bech32-encoded X25519 secret: `AGE-SECRET-KEY-1…`. Keep private. */
  secret: string;
  /** bech32-encoded X25519 recipient: `age1…`. Share with the parent PWA. */
  recipient: string;
}

/** Generate a fresh X25519 endpoint keypair. */
export async function generateEndpointKeypair(): Promise<EndpointKeypair> {
  const secret = await generateX25519Identity();
  const recipient = await identityToRecipient(secret);
  return { secret, recipient };
}

/** Derive the bech32 recipient (`age1…`) from a bech32 secret (`AGE-SECRET-KEY-1…`). */
export async function recipientFromBech32Secret(
  secret: string,
): Promise<string> {
  return identityToRecipient(secret);
}

/**
 * Wrap the 32-byte family key for the given endpoint recipient. The resulting
 * bytes are an age v1 file that only the corresponding secret can decrypt.
 */
export async function wrapFamilyKeyForEndpoint(
  familyKey: Uint8Array,
  endpointRecipient: string,
): Promise<Uint8Array> {
  if (familyKey.length !== 32) {
    throw new Error(
      `family key must be 32 bytes, got ${familyKey.length.toString()}`,
    );
  }
  const e = new Encrypter();
  e.addRecipient(endpointRecipient);
  return e.encrypt(familyKey);
}

/**
 * Unwrap a family-key delivery using the endpoint's bech32 secret. Rejects
 * with a Decrypter error if the ciphertext was not wrapped for this endpoint.
 */
export async function unwrapFamilyKeyForEndpoint(
  endpointSecret: string,
  ciphertext: Uint8Array,
): Promise<Uint8Array> {
  const d = new Decrypter();
  d.addIdentity(endpointSecret);
  const fk = await d.decrypt(ciphertext);
  if (fk.length !== 32) {
    throw new Error(
      `unwrapped payload must be 32 bytes, got ${fk.length.toString()}`,
    );
  }
  return fk;
}
